/**
 * Frontend Analytics Tracker — Layer 1 of Platform Usage Analytics.
 *
 * Emits typed first-party events to POST /v1/analytics/events. Used by:
 *   - <RouteTracker /> for automatic page_view events on every navigation
 *   - hand-instrumented call sites for feature_used / workflow_* / etc.
 *
 * BEHAVIOR
 * --------
 *   - Buffers events in memory; flushes every 5 s OR when buffer hits 20,
 *     whichever first.
 *   - Uses navigator.sendBeacon on `pagehide` to flush remaining events
 *     before the tab closes. Best-effort only — heartbeats are the
 *     source of truth for session duration.
 *   - Generates a `clientEventId` UUID per event. Same UUID rides through
 *     a retry, AND retries always preserve `occurredAt` (the original
 *     QueuedEvent is re-flushed verbatim). The server enforces dedup via
 *     a unique constraint that is currently single-column on
 *     `client_event_id`, and becomes composite `(client_event_id,
 *     occurred_at)` once the partition-audit-tables migration applies.
 *     Either constraint catches the retry, so the server insert uses
 *     untargeted `ON CONFLICT DO NOTHING` to stay schema-agnostic across
 *     the migration window.
 *   - Session id persists in sessionStorage — survives reload, dies on
 *     tab close. Drives session_start / heartbeat / session_end events.
 *   - Heartbeat every 30 s while document.visibilityState === 'visible'
 *     AND user has been active in the last 5 minutes. Time-spent on the
 *     dashboard is heartbeat × 30 s, labelled "Estimated Active Time".
 *   - 4xx response → drop the batch (forge-able anyway). 5xx / network
 *     error → retry ONCE on the next flush, carrying the same
 *     clientEventIds; then drop.
 *
 * PRIVACY
 * -------
 * Tracker code never includes free-text user content (chat messages,
 * email, names) in `properties`. The taxonomy in @fias/analytics-types
 * is the contract — reviewers reject PRs that smuggle PII.
 */

import {
  type EventType,
  type ArcheOpenedSource,
  type SessionEndReason,
  isAllowedEventName,
} from '@fias/analytics-types';
import { sessionCredentials } from './embed-document';

// =============================================================================
// Configuration
// =============================================================================

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_BATCH_THRESHOLD = 20;
const HEARTBEAT_INTERVAL_MS = 30_000;
const IDLE_THRESHOLD_MS = 5 * 60_000;
const SESSION_STORAGE_KEY = 'fias_analytics_session_id';

interface InitOptions {
  /** Base URL for the analytics endpoint. Defaults to '/v1'. */
  apiBase?: string;
  /** Callback that returns the current user_id, or null if anonymous. */
  getUserId?: () => string | null;
}

interface QueuedEvent {
  clientEventId: string;
  eventType: EventType;
  eventName: string;
  occurredAt: string;
  sessionId: string;
  pagePath?: string;
  archeId?: string;
  properties?: Record<string, unknown>;
}

// =============================================================================
// Module state (intentionally not React state — analytics outlives any
// individual component)
// =============================================================================

let apiBase = '/v1';
let userIdProvider: (() => string | null) | null = null;
let sessionId = '';
let initialized = false;
let buffer: QueuedEvent[] = [];
let pendingRetry: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let lastActivityTs = Date.now();
let currentPagePath: string | undefined;
let currentArcheId: string | undefined;

// =============================================================================
// Helpers
// =============================================================================

function generateUuid(): string {
  // crypto.randomUUID() is available in all modern browsers and happy-dom v15+.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: pseudo-random UUIDv4 (only used in environments without crypto;
  // never in production browsers).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function generateSessionId(): string {
  // sess_ + 16 hex chars (8 random bytes). Distinct from clientEventId UUIDs
  // so they're not confused in logs.
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 8; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `sess_${hex}`;
}

function readOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage can throw under SSR / private mode. Fall back to a
    // per-page-load id; we lose tab-survives-reload but keep tracking.
    return generateSessionId();
  }
}

// =============================================================================
// Init / lifecycle
// =============================================================================

/**
 * Initialize the tracker. Call once before rendering. Safe to call again —
 * subsequent calls just update the user-id provider.
 */
export function initAnalytics(opts: InitOptions = {}): void {
  apiBase = opts.apiBase ?? '/v1';
  if (opts.getUserId) {
    userIdProvider = opts.getUserId;
  }

  if (initialized) return;
  initialized = true;

  sessionId = readOrCreateSessionId();

  // Best-effort browser-only wiring. In SSR / Node tests, `window` is absent.
  if (typeof window === 'undefined') return;

  // Lifecycle: emit session_start once per browser-session lifetime.
  // We tag it onto the FIRST flush so we know the session started even if
  // the page closes before any other event fires.
  enqueue({
    clientEventId: generateUuid(),
    eventType: 'session_start',
    eventName: 'session_start',
    occurredAt: new Date().toISOString(),
    sessionId,
    pagePath: currentPagePath,
    archeId: currentArcheId,
  });

  // Activity tracking — keeps heartbeat from firing for an idle tab.
  for (const ev of ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const) {
    window.addEventListener(ev, markActive, { passive: true });
  }

  // Visibility change — pause/resume heartbeat naturally.
  document.addEventListener('visibilitychange', markActive);

  // pagehide fires more reliably than beforeunload across mobile / bfcache.
  // Use sendBeacon for the final flush so it survives the tab teardown.
  window.addEventListener('pagehide', flushOnUnload);

  startTimers();
}

/** Test-only reset — drops all module state. */
export function _resetAnalyticsForTests(): void {
  apiBase = '/v1';
  userIdProvider = null;
  sessionId = '';
  initialized = false;
  buffer = [];
  pendingRetry = [];
  if (flushTimer !== null) clearTimeout(flushTimer);
  if (heartbeatTimer !== null) clearInterval(heartbeatTimer);
  flushTimer = null;
  heartbeatTimer = null;
  lastActivityTs = Date.now();
  currentPagePath = undefined;
  currentArcheId = undefined;
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function markActive(): void {
  lastActivityTs = Date.now();
}

function startTimers(): void {
  if (flushTimer === null) {
    flushTimer = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
  }
  if (heartbeatTimer === null) {
    heartbeatTimer = setInterval(() => {
      tickHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  }
}

function tickHeartbeat(): void {
  // Skip if hidden or idle. Both conditions guard against under-counting
  // being miscounted as user-present time.
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (Date.now() - lastActivityTs > IDLE_THRESHOLD_MS) return;
  enqueue({
    clientEventId: generateUuid(),
    eventType: 'session_heartbeat',
    eventName: 'session_heartbeat',
    occurredAt: new Date().toISOString(),
    sessionId,
    pagePath: currentPagePath,
    archeId: currentArcheId,
  });
}

// =============================================================================
// Buffering + flush
// =============================================================================

function enqueue(evt: QueuedEvent): void {
  if (!initialized) return;
  buffer.push(evt);
  if (buffer.length >= FLUSH_BATCH_THRESHOLD) {
    void flush();
  }
}

async function flush(): Promise<void> {
  if (buffer.length === 0 && pendingRetry.length === 0) return;

  // Take ownership of the current buffer + any pending retry batch.
  const batch = [...pendingRetry, ...buffer];
  buffer = [];
  pendingRetry = [];

  try {
    const res = await fetch(`${apiBase}/analytics/events`, {
      method: 'POST',
      credentials: sessionCredentials(),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
    });
    if (res.status >= 500) {
      // 5xx — retry once on the next flush.
      pendingRetry = batch;
      return;
    }
    // 2xx and 4xx → drop. 4xx data is forge-able anyway and we don't
    // want to spin on a rejected payload. The server returns granular
    // diagnostics in the body but the tracker doesn't act on them.
  } catch {
    // Network error — same retry behavior as 5xx.
    pendingRetry = batch;
  }
}

function flushOnUnload(): void {
  if (!initialized) return;

  // Best-effort lifecycle hint. Heartbeats remain the source of truth
  // for session duration; session_end is helpful context for "did the
  // user actually close the tab vs. crash?" but is not relied on for
  // any duration calculation. May fire on bfcache hide and again on
  // real close — the dashboard can dedupe by (sessionId, eventName).
  const endEvent: QueuedEvent = {
    clientEventId: generateUuid(),
    eventType: 'session_end',
    eventName: 'unload',
    occurredAt: new Date().toISOString(),
    sessionId,
    pagePath: currentPagePath,
    archeId: currentArcheId,
  };

  const batch = [...pendingRetry, ...buffer, endEvent];
  buffer = [];
  pendingRetry = [];

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify({ events: batch })], {
        type: 'application/json',
      });
      navigator.sendBeacon(`${apiBase}/analytics/events`, blob);
      return;
    }
  } catch {
    // sendBeacon can throw if the blob is too large or the URL invalid.
    // We deliberately don't fall back to fetch() here — the page is going
    // away and any in-flight fetch will be cancelled by the browser.
  }
}

// =============================================================================
// Page tracking — used by RouteTracker, also exposed for manual control
// (e.g., a single-page widget that wants to track sub-views).
// =============================================================================

export function setCurrentPage(path: string, archeId?: string): void {
  currentPagePath = path;
  currentArcheId = archeId;
}

// =============================================================================
// Public API — the typed call sites engineers use across the app
// =============================================================================

/**
 * Generic emitter. Most call sites should use the typed shortcuts below;
 * `track` is the escape hatch when a shortcut doesn't fit.
 */
export function track(
  eventType: EventType,
  eventName: string,
  options: {
    properties?: Record<string, unknown>;
    archeId?: string;
    pagePath?: string;
  } = {},
): void {
  // Soft client-side allow-list check. The server enforces the same list,
  // so an unknown name will be dropped server-side anyway — this just gives
  // engineers fast feedback during development.
  if (!isAllowedEventName(eventType, eventName)) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[analytics] ignoring event "${eventName}" — not in @fias/analytics-types allow-list for type "${eventType}"`,
      );
    }
    return;
  }
  enqueue({
    clientEventId: generateUuid(),
    eventType,
    eventName,
    occurredAt: new Date().toISOString(),
    sessionId,
    pagePath: options.pagePath ?? currentPagePath,
    archeId: options.archeId ?? currentArcheId,
    properties: options.properties,
  });
}

/**
 * Map raw URL paths to the normalized page CATEGORY in
 * EVENT_NAMES_BY_TYPE.page_view. The full URL goes in `pagePath`; this
 * is just the bucket the dashboard rolls up by.
 */
function normalizePagePath(path: string): string {
  if (path === '/' || path === '/home') return 'home';
  if (
    path.startsWith('/auth') ||
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/forgot-password') ||
    path.startsWith('/reset-password')
  ) {
    return 'auth';
  }
  if (path === '/arches' || path === '/marketplace') return 'arche_directory';
  if (/^\/a\/[^/]+/.test(path)) return 'arche_view';
  if (path === '/dev-console' || path === '/dev-console/') return 'dev_console';
  if (path.startsWith('/dev-console/')) return 'dev_console_tool';
  if (path.startsWith('/profile')) return 'profile';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/submissions') || path.includes('/submissions')) return 'submissions';
  return 'other';
}

export const analytics = {
  init: initAnalytics,
  setCurrentPage,
  track,

  pageView(path: string, archeId?: string): void {
    setCurrentPage(path, archeId);
    enqueue({
      clientEventId: generateUuid(),
      eventType: 'page_view',
      eventName: normalizePagePath(path),
      occurredAt: new Date().toISOString(),
      sessionId,
      pagePath: path,
      archeId,
    });
  },

  featureUsed(featureName: string, properties?: Record<string, unknown>): void {
    track('feature_used', featureName, { properties });
  },

  archeOpened(archeId: string, source: ArcheOpenedSource): void {
    enqueue({
      clientEventId: generateUuid(),
      eventType: 'arche_opened',
      eventName: 'arche_opened',
      occurredAt: new Date().toISOString(),
      sessionId,
      pagePath: currentPagePath,
      archeId,
      properties: { source },
    });
  },

  archeLeft(archeId: string, durationMs: number): void {
    enqueue({
      clientEventId: generateUuid(),
      eventType: 'arche_left',
      eventName: 'arche_left',
      occurredAt: new Date().toISOString(),
      sessionId,
      pagePath: currentPagePath,
      archeId,
      properties: { durationMs },
    });
  },

  workflowStarted(name: string): void {
    track('workflow_started', name);
  },

  workflowCompleted(name: string, durationMs?: number): void {
    track('workflow_completed', name, {
      properties: durationMs !== undefined ? { durationMs } : undefined,
    });
  },

  workflowAbandoned(name: string, lastStep?: string, durationMs?: number): void {
    const properties: Record<string, unknown> = {};
    if (lastStep !== undefined) properties.lastStep = lastStep;
    if (durationMs !== undefined) properties.durationMs = durationMs;
    track('workflow_abandoned', name, {
      properties: Object.keys(properties).length > 0 ? properties : undefined,
    });
  },

  chatMessageSent(archeId: string): void {
    enqueue({
      clientEventId: generateUuid(),
      eventType: 'chat_message_sent',
      eventName: 'chat_message_sent',
      occurredAt: new Date().toISOString(),
      sessionId,
      pagePath: currentPagePath,
      archeId,
    });
  },

  purchaseInitiated(sku: string): void {
    track('purchase_initiated', mapSkuToCategory(sku), { properties: { sku } });
  },

  /**
   * Funnel signal — client believes the purchase succeeded. NOT
   * authoritative: the dashboard joins this against the server-side
   * payment_history table for the real conversion count.
   */
  purchaseFlowCompleted(sku: string): void {
    track('purchase_flow_completed', mapSkuToCategory(sku), { properties: { sku } });
  },

  /**
   * Best-effort session_end. Heartbeats remain the source of truth for
   * session duration; this is just a lifecycle hint.
   */
  sessionEnd(reason: SessionEndReason): void {
    enqueue({
      clientEventId: generateUuid(),
      eventType: 'session_end',
      eventName: reason,
      occurredAt: new Date().toISOString(),
      sessionId,
      pagePath: currentPagePath,
      archeId: currentArcheId,
    });
  },

  /** Test-only forced flush. */
  _flushForTests: flush,
};

function mapSkuToCategory(sku: string): 'credits' | 'subscription' | 'arche' {
  if (sku.startsWith('sub_') || sku.includes('subscription')) return 'subscription';
  if (sku.startsWith('arche_') || sku.includes('arche')) return 'arche';
  return 'credits';
}

// Allow the ID-provider to be inspected by tests (and by code that
// relies on it during build-out — kept minimal and intentional).
export function _getUserIdForTests(): string | null {
  return userIdProvider ? userIdProvider() : null;
}
